import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL, Back_url } from "../config";

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  cancelBg: "#d3be76",
  cancelHover: "#c2ad65",
};

interface CarPhoto {
  id: number;
  car_id: number;
  path: string;
  is_main: boolean;
}

interface IconProps {
  size?: number;
  color?: string;
}

const iconBase = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

const UploadCloudIcon: React.FC<IconProps> = ({ size = 28, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M16 16l-4-4-4 4" />
    <path d="M12 12v9" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const XIcon: React.FC<IconProps> = ({ size = 14, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{
              width: 200,
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: 60,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 280,
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
        <div style={{
          width: 150,
          height: 42,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 8,
          animation: "shimmer 1.5s infinite"
        }}></div>
      </div>

      {/* Main Content Skeleton */}
      <div className="row g-4">
        <div className="col-12 col-lg-8">
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
          }}>
            {/* General Information Skeleton */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{
                width: 180,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <div style={{
                      width: 80,
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      marginBottom: 6,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{
                      width: "100%",
                      height: 42,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 8,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Registration Skeleton */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{
                width: 140,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[...Array(2)].map((_, i) => (
                  <div key={i}>
                    <div style={{
                      width: 100,
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      marginBottom: 6,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{
                      width: "100%",
                      height: 42,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 8,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Skeleton */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{
                width: 130,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                {[...Array(3)].map((_, i) => (
                  <div key={i}>
                    <div style={{
                      width: 80,
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      marginBottom: 6,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{
                      width: "100%",
                      height: 42,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 8,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status & Maintenance Skeleton */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{
                width: 160,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[...Array(2)].map((_, i) => (
                  <div key={i}>
                    <div style={{
                      width: 80,
                      height: 12,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      marginBottom: 6,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{
                      width: "100%",
                      height: 42,
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                      backgroundSize: "200% 100%",
                      borderRadius: 8,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Photos Skeleton */}
            <div>
              <div style={{
                width: 120,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: "100%",
                height: 120,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 10,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>

        {/* Sidebar Skeleton */}
        <div className="col-12 col-lg-4">
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
          }}>
            <div style={{
              width: 80,
              height: 18,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              marginBottom: 16,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: "100%",
              height: 100,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 8,
              marginBottom: 16,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: "100%",
              height: 42,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 8,
              marginBottom: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: "100%",
              height: 42,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 8,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
        </div>
      </div>
    </div>
  );
}

const EditCar = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState("");
  const [model, setModel] = useState("");
  const [type, setType] = useState("");
  const [number, setNumber] = useState("");
  const [engine_capacity, setEngineCapacity] = useState("");
  const [color, setColor] = useState("");
  const [price_per_day, setPricePerDay] = useState("");
  const [price_per_month, setPricePerMonth] = useState("");
  const [price_per_year, setPricePerYear] = useState("");
  const [is_available_for_rent, setIsAvailableForRent] = useState(true);
  const [registration_number, setRegistrationNumber] = useState("");
  const [registration_year, setRegistrationYear] = useState("");
  const [is_in_maintenance, setIsInMaintenance] = useState(false);
  const [maintenance_days_estimate, setMaintenanceDaysEstimate] = useState("");
  const [photos, setPhotos] = useState<CarPhoto[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const handleClickUpload = () => {
    inputRef.current?.click();
  };

  const handleFiles = (selectedFiles: FileList | null) => {
    if (selectedFiles) {
      setFiles((prev) => [...prev, ...Array.from(selectedFiles)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!String(name).trim()) newErrors.name = "Name is required";
    if (!String(model).trim()) newErrors.model = "Model is required";
    if (!String(type).trim()) newErrors.type = "Transmission type is required";
    if (!String(number).trim()) newErrors.number = "Number of seats is required";
    if (!String(color).trim()) newErrors.color = "Color is required";
    if (!String(engine_capacity).trim()) newErrors.engine_capacity = "Engine capacity is required";
    if (!String(price_per_day).trim()) newErrors.price_per_day = "Price per day is required";
    if (!String(registration_number).trim()) newErrors.registration_number = "Registration number is required";
    if (!String(registration_year).trim()) newErrors.registration_year = "Registration year is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("model", model);
      formData.append("type", type);
      formData.append("number", number);
      formData.append("engine_capacity", engine_capacity);
      formData.append("color", color);
      formData.append("price_per_day", price_per_day);
      formData.append("price_per_month", price_per_month);
      formData.append("price_per_year", price_per_year);
      formData.append("is_available_for_rent", is_available_for_rent ? "1" : "0");
      formData.append("registration_number", registration_number);
      formData.append("registration_year", registration_year);
      formData.append("is_in_maintenance", is_in_maintenance ? "1" : "0");
      formData.append("maintenance_days_estimate", maintenance_days_estimate);

      files.forEach((file) => {
        formData.append("photos[]", file);
      });

      formData.append("_method", "PUT");

      await axios.post(`${API_URL}/car/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...config_token.headers,
        },
      });

      toast.success("Car updated successfully!");
      navigate("/admin/list-cars");
    } catch (error: any) {
      console.error("Error updating car:", error.response?.data || error.message);
      toast.error("Error updating car. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    setLoadingData(true);
    axios
      .get(`${API_URL}/cars/${id}`, config_token)
      .then((response) => {
        const car = response.data.data || response.data;
        if (!car) {
          console.error("car data is undefined");
          return;
        }
        setName(car.name || "");
        setModel(car.model || "");
        setNumber(car.number || "");
        setType(car.type || "");
        setColor(car.color || "");
        setPricePerDay(car.price_per_day || "");
        setPricePerMonth(car.price_per_month || "");
        setPricePerYear(car.price_per_year || "");
        setEngineCapacity(car.engine_capacity || "");
        setIsAvailableForRent(car.is_available_for_rent ?? true);
        setRegistrationNumber(car.registration_number || "");
        setRegistrationYear(car.registration_year != null ? String(car.registration_year) : "");
        setIsInMaintenance(car.is_in_maintenance ?? false);
        setMaintenanceDaysEstimate(car.maintenance_days_estimate?.toString() || "");
        setPhotos(car.photos || []);
      })
      .catch((error) => {
        console.error("Error loading car:", error);
        toast.error("Error loading car data");
      })
      .finally(() => {
        setLoadingData(false);
      });
  }, [id]);

  const inputStyle = (hasError?: boolean): React.CSSProperties => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    border: `1px solid ${hasError ? colors.danger : colors.border}`,
    borderRadius: 8,
    fontSize: 13,
    color: colors.text,
    outline: "none",
    background: "#fff",
  });

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: colors.textMuted,
    marginBottom: 6,
  };

  const sectionHeadingStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: colors.text,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: `1px solid ${colors.borderLight}`,
  };

  if (loadingData) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px 20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* SECTION EN-TÊTE */}
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: "13px", color: "#64748b" }}>
                <span style={{ color: "#64748b", fontWeight: 500 }}>Cars</span>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: "10px", color: "#94a3b8" }}></i>
                <span style={{ color: "#294b5a", fontWeight: 600 }}>Edit Car</span>
              </div>
              <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Edit Vehicle</h1>
              <p style={{ color: "#64748b", fontSize: "13.5px", margin: "4px 0 0" }}>
                Update the details of <strong>{name || "this car"}</strong>
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/list-cars")}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#475569",
                fontSize: "13.5px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#0f172a";
                e.currentTarget.style.borderColor = "#94a3b8";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Car List
            </button>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                {/* Formulaire Principal */}
                <div
                  style={{
                    background: "#fff",
                    border: `0.5px solid ${colors.borderLight}`,
                    borderRadius: 12,
                    padding: 24,
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                  }}
                >
                  {/* General Information */}
                  <div>
                    <h3 style={sectionHeadingStyle}>General Information</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Name *</label>
                        <input
                          type="text"
                          placeholder="e.g. Golf 7"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          style={inputStyle(!!errors.name)}
                        />
                        {errors.name && <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.name}</span>}
                      </div>

                      <div>
                        <label style={labelStyle}>Model *</label>
                        <input
                          type="text"
                          placeholder="e.g. Volkswagen"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          style={inputStyle(!!errors.model)}
                        />
                        {errors.model && <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.model}</span>}
                      </div>

                      <div>
                        <label style={labelStyle}>Number of Seats *</label>
                        <input
                          type="number"
                          placeholder="e.g. 5"
                          value={number}
                          onChange={(e) => setNumber(e.target.value)}
                          style={inputStyle(!!errors.number)}
                        />
                        {errors.number && <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.number}</span>}
                      </div>

                      <div>
                        <label style={labelStyle}>Color *</label>
                        <input
                          type="text"
                          placeholder="e.g. Black"
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          style={inputStyle(!!errors.color)}
                        />
                        {errors.color && <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.color}</span>}
                      </div>

                      <div>
                        <label style={labelStyle}>Engine Capacity *</label>
                        <input
                          type="text"
                          placeholder="e.g. 2.0L TDI"
                          value={engine_capacity}
                          onChange={(e) => setEngineCapacity(e.target.value)}
                          style={inputStyle(!!errors.engine_capacity)}
                        />
                        {errors.engine_capacity && <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.engine_capacity}</span>}
                      </div>

                      <div>
                        <label style={labelStyle}>Transmission Type *</label>
                        <select
                          value={type}
                          onChange={(e) => setType(e.target.value)}
                          style={inputStyle(!!errors.type)}
                        >
                          <option value="">Select transmission</option>
                          <option value="manual">Manual</option>
                          <option value="automatic">Automatic</option>
                        </select>
                        {errors.type && <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.type}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Registration Information */}
                  <div>
                    <h3 style={sectionHeadingStyle}>Registration</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Registration Number *</label>
                        <input
                          type="text"
                          placeholder="e.g. 12345-A-6"
                          value={registration_number}
                          onChange={(e) => setRegistrationNumber(e.target.value)}
                          style={inputStyle(!!errors.registration_number)}
                        />
                        {errors.registration_number && (
                          <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.registration_number}</span>
                        )}
                      </div>

                      <div>
                        <label style={labelStyle}>Registration Year *</label>
                        <input
                          type="number"
                          placeholder="e.g. 2022"
                          value={registration_year}
                          onChange={(e) => setRegistrationYear(e.target.value)}
                          style={inputStyle(!!errors.registration_year)}
                        />
                        {errors.registration_year && (
                          <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.registration_year}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pricing Information */}
                  <div>
                    <h3 style={sectionHeadingStyle}>Pricing Rates</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Price / Day ($) *</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={price_per_day}
                          onChange={(e) => setPricePerDay(e.target.value)}
                          style={inputStyle(!!errors.price_per_day)}
                        />
                        {errors.price_per_day && (
                          <span style={{ fontSize: 11, color: colors.danger, marginTop: 4, display: "block" }}>{errors.price_per_day}</span>
                        )}
                      </div>

                      <div>
                        <label style={labelStyle}>Price / Month ($)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={price_per_month}
                          onChange={(e) => setPricePerMonth(e.target.value)}
                          style={inputStyle()}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Price / Year ($)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={price_per_year}
                          onChange={(e) => setPricePerYear(e.target.value)}
                          style={inputStyle()}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Status & Maintenance */}
                  <div>
                    <h3 style={sectionHeadingStyle}>Status & Maintenance</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Available for Rent</label>
                        <select
                          value={is_available_for_rent.toString()}
                          onChange={(e) => {
                            const newValue = e.target.value === 'true';
                            setIsAvailableForRent(newValue);
                            if (newValue) {
                              setIsInMaintenance(false);
                              setMaintenanceDaysEstimate("");
                            }
                          }}
                          style={inputStyle()}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>

                      <div>
                        <label style={labelStyle}>In Maintenance</label>
                        <select
                          value={is_in_maintenance.toString()}
                          onChange={(e) => {
                            const newValue = e.target.value === 'true';
                            setIsInMaintenance(newValue);
                            if (newValue) {
                              setIsAvailableForRent(false);
                            }
                          }}
                          style={inputStyle()}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </div>

                      {is_in_maintenance && (
                        <div style={{ gridColumn: "span 2" }}>
                          <label style={labelStyle}>Estimated Maintenance Days</label>
                          <input
                            type="number"
                            placeholder="e.g. 5"
                            value={maintenance_days_estimate}
                            onChange={(e) => setMaintenanceDaysEstimate(e.target.value)}
                            min="1"
                            max="365"
                            style={inputStyle()}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Photos Upload */}
                  <div>
                    <h3 style={sectionHeadingStyle}>Car Photos</h3>

                    {/* Existing Photos */}
                    {photos.length > 0 && (
                      <div style={{ marginBottom: "16px" }}>
                        <label style={labelStyle}>Current Photos</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                          {photos.map((photo) => (
                            <div
                              key={photo.id}
                              style={{
                                width: 80,
                                height: 80,
                                borderRadius: 8,
                                overflow: "hidden",
                                position: "relative",
                                border: `1px solid ${colors.borderLight}`,
                                background: "#fff",
                              }}
                            >
                              <img
                                src={`${Back_url}/${photo.path}`}
                                alt="car-photo"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                              {photo.is_main && (
                                <span
                                  style={{
                                    position: "absolute",
                                    bottom: 4,
                                    left: 4,
                                    backgroundColor: "#059669",
                                    color: "white",
                                    padding: "1px 6px",
                                    borderRadius: 3,
                                    fontSize: "9px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Main
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div
                      onClick={handleClickUpload}
                      style={{
                        border: `2px dashed ${errors.photos ? colors.danger : colors.border}`,
                        borderRadius: 10,
                        padding: "24px 16px",
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: colors.bgSubtle,
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                        e.currentTarget.style.borderColor = errors.photos ? colors.danger : colors.primary;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = colors.bgSubtle;
                        e.currentTarget.style.borderColor = errors.photos ? colors.danger : colors.border;
                      }}
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        ref={inputRef}
                        style={{ display: "none" }}
                        onChange={(e) => handleFiles(e.target.files)}
                      />
                      <div style={{ color: colors.textMuted, marginBottom: 6, display: "flex", justifyContent: "center" }}>
                        <UploadCloudIcon color={colors.primary} />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: colors.text, marginBottom: 2 }}>
                        Click or drag images to upload
                      </div>
                      <div style={{ fontSize: 11, color: colors.textFaint }}>PNG, JPG, JPEG up to 10MB</div>
                    </div>

                    {/* Grid des nouvelles images uploadées */}
                    {files.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
                        {files.map((file, idx) => (
                          <div
                            key={idx}
                            style={{
                              width: 72,
                              height: 72,
                              borderRadius: 8,
                              overflow: "hidden",
                              position: "relative",
                              border: `1px solid ${colors.borderLight}`,
                              background: "#fff",
                            }}
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                background: "rgba(239, 68, 68, 0.85)",
                                color: "#fff",
                                border: "none",
                                borderRadius: "50%",
                                width: 18,
                                height: 18,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                              }}
                            >
                              <XIcon size={12} color="#fff" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SIDEBAR - Actions & Résumé */}
              <div className="col-12 col-lg-4">
                <div
                  style={{
                    background: "#fff",
                    border: `0.5px solid ${colors.borderLight}`,
                    borderRadius: 12,
                    padding: 24,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: colors.text }}>Actions</h3>
                  <p style={{ fontSize: 12, color: colors.textFaint, margin: 0 }}>
                    {files.length} new photo(s) to upload
                  </p>

                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "12px 16px", border: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "12.5px" }}>
                      <span style={{ color: "#64748b" }}>Name</span>
                      <span style={{ fontWeight: 600, color: "#0f172a", maxWidth: "120px", textAlign: "right" }} className="text-truncate">
                        {name || "—"}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "12.5px" }}>
                      <span style={{ color: "#64748b" }}>Model</span>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{model || "—"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "12.5px" }}>
                      <span style={{ color: "#64748b" }}>Price / Day</span>
                      <span style={{ fontWeight: 600, color: "#294b5a" }}>${price_per_day || "0.00"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "12.5px" }}>
                      <span style={{ color: "#64748b" }}>Status</span>
                      <span style={{ fontWeight: 600, color: is_available_for_rent ? "#059669" : "#dc2626" }}>
                        {is_available_for_rent ? "Available" : "Unavailable"}
                      </span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    onClick={handleSubmit}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      borderRadius: 8,
                      border: "none",
                      background: loading ? colors.textFaint : colors.primary,
                      color: "#fff",
                      fontSize: 13.5,
                      fontWeight: 600,
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: loading ? "none" : "0 2px 8px rgba(41, 75, 90, 0.25)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) e.currentTarget.style.background = colors.primaryHover;
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) e.currentTarget.style.background = colors.primary;
                    }}
                  >
                    <i className="fa-solid fa-save" style={{ color: "#d3be76", fontSize: "13px" }}></i>
                    {loading ? "Updating..." : "Update Car"}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/admin/list-cars")}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      borderRadius: 8,
                      border: "none",
                      background: colors.cancelBg,
                      color: "#fff",
                      fontSize: 13.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = colors.cancelHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = colors.cancelBg)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditCar;